import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { ScanLang, translations, formatScanString } from '../i18n/translations';

type LanguageContextType = {
  language: ScanLang;
  setLanguage: (lang: ScanLang) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function readInitialLanguage(): ScanLang {
  if (typeof window === 'undefined') return 'en';
  const q = new URLSearchParams(window.location.search).get('lang');
  if (q === 'en' || q === 'ko') return q;
  const saved = localStorage.getItem('voltron_scan_lang');
  if (saved === 'en' || saved === 'ko') return saved;
  return 'en';
}

function persistLanguage(lang: ScanLang) {
  localStorage.setItem('voltron_scan_lang', lang);
  document.documentElement.lang = lang;
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('lang', lang);
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    /* ignore */
  }
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<ScanLang>(readInitialLanguage);

  const setLanguage = (lang: ScanLang) => {
    setLanguageState(lang);
    persistLanguage(lang);
  };

  useEffect(() => {
    persistLanguage(language);
  }, [language]);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('lang');
    if (q === 'en' || q === 'ko') setLanguageState(q);
  }, []);

  const t = (key: string, vars?: Record<string, string | number>) => {
    const raw = translations[language][key] || translations.en[key] || key;
    return vars ? formatScanString(raw, vars) : raw;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useScanLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useScanLanguage must be used within LanguageProvider');
  return ctx;
}
